// src/pages/Pay.tsx (통째로 교체용 최종본)
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, FileText, Info, Plus, RefreshCw, Search, X } from "lucide-react";
import "../styles/payment-design.css";
import "../styles/pay-mobile.css";
import { authFetch, renewLoginIfNeeded } from "../lib/auth";
import { displayServiceName } from "../lib/serviceLabel";

type PaymentStatus = "requested" | "approved" | "complete" | "failed" | "rejected";

type Payment = {
  id: number;
  order_id: string;
  title: string;

  /** ✅ 원금 */
  amount: number;

  /** ✅ 분할결제 정보 */
  group_id?: number | null;
  split_index?: number | null;
  split_count?: number | null;
  base_order_id?: string | null;

  /** ✅ DB 표준(권장): fee_amount=수수료만, vat_amount=부가세, total_amount=총액 */
  fee_amount?: number | null;
  vat_amount?: number | null;
  total_amount?: number | null;

  /** ✅ 과거/호환 필드(있으면 대응) */
  totalAmount?: number | null;
  total?: number | null;
  pay_amount?: number | null;
  fee?: number | null;
  vat?: number | null;

  /** ✅ 서버에서 내려주는 "토큰 포함 결제 URL" (있으면 우선 사용) */
  pay_url_with_token?: string | null;

  status: PaymentStatus;
  approval_status?: string | null;
  reject_reason?: string | null;
  meta?: any;

  /** ✅ 화면에 표시하고 싶은 필드들(서버가 내려주면 사용) */
  category?: string | null;
  counterparty_name?: string | null;
  account_holder?: string | null;
  receiver_name?: string | null;
  depositor_name?: string | null;

  created_at: string;
};

type TabKey = "all" | "requested" | "approved" | "complete" | "failed";

const TAB_LABEL: Record<TabKey, string> = {
  all: "전체",
  requested: "대기",
  approved: "결제 가능",
  complete: "완료",
  failed: "요청철회/거절",
};

function statusMeta(status?: string | null) {
  const s = String(status || "").toLowerCase();

  switch (s) {
    case "complete":
    case "completed":
      return { label: "완료", cls: "bg-emerald-50 text-emerald-700 ring-emerald-100" };
    case "approved":
      return { label: "결제 가능", cls: "bg-sky-50 text-sky-700 ring-sky-100" };
    case "rejected":
    case "failed":
      return { label: "요청철회/거절", cls: "bg-rose-50 text-rose-700 ring-rose-100" };
    case "requested":
    case "pending":
    default:
      return { label: "대기", cls: "bg-amber-50 text-amber-700 ring-amber-100" };
  }
}

function toNum(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function getAmountBreakdown(p: Payment) {
  const principal = toNum(p.amount) ?? 0;

  const total =
    toNum(p.total_amount) ??
    toNum(p.totalAmount) ??
    toNum(p.total) ??
    toNum(p.pay_amount);

  const feeOnlyCandidate = toNum(p.fee_amount) ?? toNum(p.fee);
  const vatCandidate = toNum(p.vat_amount) ?? toNum(p.vat);

  if (total !== null) {
    const diff = Math.max(0, total - principal);

    let feeForDisplay: number | null = null;
    let vatForDisplay: number | null = null;

    if (feeOnlyCandidate !== null && vatCandidate !== null) {
      if (Math.abs(feeOnlyCandidate + vatCandidate - diff) <= 1) {
        feeForDisplay = feeOnlyCandidate;
        vatForDisplay = vatCandidate;
      } else if (Math.abs(feeOnlyCandidate - diff) <= 1) {
        vatForDisplay = vatCandidate;
        feeForDisplay = Math.max(0, diff - vatForDisplay);
      } else {
        feeForDisplay = feeOnlyCandidate;
        vatForDisplay = vatCandidate;
      }
    } else if (vatCandidate !== null) {
      vatForDisplay = vatCandidate;
      feeForDisplay = Math.max(0, diff - vatForDisplay);
    } else if (feeOnlyCandidate !== null) {
      feeForDisplay = feeOnlyCandidate;
      vatForDisplay = null;
    }

    return {
      principal,
      fee: feeForDisplay,
      vat: vatForDisplay,
      total,
      hasTotal: true,
      hasBreakdown: feeForDisplay !== null || vatForDisplay !== null,
    };
  }

  if (feeOnlyCandidate !== null || vatCandidate !== null) {
    const t = principal + (feeOnlyCandidate ?? 0) + (vatCandidate ?? 0);
    return {
      principal,
      fee: feeOnlyCandidate,
      vat: vatCandidate,
      total: t,
      hasTotal: true,
      hasBreakdown: true,
    };
  }

  return {
    principal,
    fee: null,
    vat: null,
    total: principal,
    hasTotal: false,
    hasBreakdown: false,
  };
}

function shouldHidePayment(p: Payment) {
  const title = String(p.title || "").replace(/\s/g, "");
  const catRaw = String(p.category || "").replace(/\s/g, "");
  const cat = catRaw.toLowerCase();

  if (title.includes("등기부등본") || title.includes("등기부")) return true;
  if (cat.includes("등기부") || cat.includes("registry")) return true;

  const maybeTotal =
    toNum(p.total_amount) ??
    toNum(p.total) ??
    toNum(p.totalAmount) ??
    toNum(p.pay_amount) ??
    0;

  if ((title.includes("발급") || title.includes("수수료")) && maybeTotal === 1200) {
    return true;
  }

  return false;
}

function pickCounterpartyName(p: Payment): string | null {
  const v =
    p.counterparty_name ||
    p.account_holder ||
    p.receiver_name ||
    p.depositor_name ||
    null;
  return v ? String(v).trim() : null;
}

function hasVatNoticeAgreed(p: Payment): boolean {
  const meta = p.meta || {};
  return !!(meta.vat_notice_agreed || meta.vat_notice_agreed_at);
}

export default function Pay() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showReceipt, setShowReceipt] = useState(() => Boolean(location.state?.requestSubmitted));
  const [query, setQuery] = useState("");

  const [items, setItems] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabKey>("all");
  const [payingId, setPayingId] = useState<number | null>(null);
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentSaving, setConsentSaving] = useState(false);
  const [consentTarget, setConsentTarget] = useState<Payment | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ✅ 신한카드 안내 배너 ON/OFF (필요 시 나중에 env로 분리 가능)
  const SHINHAN_NOTICE = true;

  const loadPayments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch("/api/payments/my");
      const j = await res.json();
      if (!res.ok || !j.ok) {
        throw new Error(j?.message || "결제요청 목록을 불러오지 못했습니다.");
      }
      setItems(j.items || []);
    } catch (e: any) {
      console.error("[Pay] load error", e);
      setError(e?.message || "목록 로딩 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadPayments();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [loadPayments]);

  useEffect(() => {
    const handler = () => {
      loadPayments().catch(() => void 0);
    };
    window.addEventListener("focus", handler);
    return () => {
      window.removeEventListener("focus", handler);
    };
  }, [loadPayments]);

  const visibleItems = useMemo(
    () => items.filter((p) => !shouldHidePayment(p)),
    [items]
  );

  const filtered = useMemo(() => {
    if (tab === "all") return visibleItems;
    if (tab === "failed") {
      return visibleItems.filter(
        (p) => p.status === "failed" || p.status === "rejected"
      );
    }
    return visibleItems.filter((p) => p.status === tab);
  }, [visibleItems, tab]);

  const counts = useMemo(() => ({
    all: visibleItems.length,
    requested: visibleItems.filter((p) => p.status === "requested").length,
    approved: visibleItems.filter((p) => p.status === "approved").length,
    complete: visibleItems.filter((p) => p.status === "complete").length,
    failed: visibleItems.filter((p) => p.status === "failed" || p.status === "rejected").length,
  }), [visibleItems]);

  const searched = useMemo(() => {
    const value = query.trim().toLocaleLowerCase();
    if (!value) return filtered;
    return filtered.filter((p) => [p.title, p.order_id, displayServiceName(p.category || ""), pickCounterpartyName(p)]
      .some((field) => String(field || "").toLocaleLowerCase().includes(value)));
  }, [filtered, query]);

  const emptyMessage = useMemo(() => {
    if (loading) return "";
    if (tab === "approved") return "결제 가능한 요청이 없습니다.";
    if (tab === "requested") return "대기 중인 결제요청이 없습니다.";
    if (tab === "complete") return "완료된 결제내역이 없습니다.";
    if (tab === "failed") return "요청철회 또는 거절된 결제내역이 없습니다.";
    return "등록된 결제요청이 없습니다.";
  }, [tab, loading]);

  const fmtAmount = (n: number) =>
    n.toLocaleString("ko-KR", { maximumFractionDigits: 0 }) + "원";

  const fmtDateTime = (iso: string) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("ko-KR");
    } catch {
      return iso;
    }
  };

  const doPay = async (p: Payment) => {
    if (payingId) return;
    setPayingId(p.id);
    setError(null);

    try {
      const token = await renewLoginIfNeeded();
      if (!token) {
        alert("로그인이 필요합니다.");
        setPayingId(null);
        return;
      }

      const fallbackUrl = `/api/payments/${p.id}/pay-form?token=${encodeURIComponent(token)}`;
      let url = p.pay_url_with_token || fallbackUrl;

      try {
        const u = new URL(url, window.location.origin);
        const host = u.host || "";
        if (host && host !== window.location.host) {
          url = fallbackUrl;
        } else {
          u.searchParams.set("token", token);
          url = u.toString();
        }
      } catch {
        url = fallbackUrl;
      }

      window.location.href = url;
    } catch (e) {
      console.error("[Pay] handlePay error", e);
      alert("결제를 시작하는 중 오류가 발생했습니다.");
      setPayingId(null);
    }
  };

  const handlePay = (p: Payment) => {
    if (payingId) return;
    if (hasVatNoticeAgreed(p)) {
      doPay(p);
      return;
    }
    setConsentTarget(p);
    setConsentChecked(false);
    setConsentOpen(true);
  };

  const handleConsentConfirm = async () => {
    if (!consentTarget) return;
    if (!consentChecked) {
      alert("동의가 필요합니다.");
      return;
    }

    setConsentSaving(true);
    try {
      const res = await authFetch(`/api/payments/${consentTarget.id}/vat-notice-agree`, {
        method: "POST",
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j?.ok) throw new Error(j?.message || "동의 저장 실패");

      setConsentOpen(false);
      setConsentTarget(null);
      setConsentChecked(false);
      doPay(consentTarget);
    } catch (e: any) {
      alert(e?.message || "동의 저장 중 오류가 발생했습니다.");
    } finally {
      setConsentSaving(false);
    }
  };

  const goEdit = (id: number) => {
    navigate(`/pay/${id}/edit`);
  };

  return (
    <div className="jp-payment-list">
      <div className="jp-payment-list-wrap">
        {showReceipt && (
          <section className="jp-payment-receipt" role="status">
            <CheckCircle2 size={30} strokeWidth={1.5} />
            <div>
              <h2>결제요청이 접수되었어요</h2>
              <p>관리자 승인 후 카드결제를 진행해 주세요.</p>
            </div>
            <button type="button" aria-label="접수 완료 안내 닫기" onClick={() => { setShowReceipt(false); navigate("/pay", { replace: true, state: null }); }}><X size={18} /></button>
          </section>
        )}

        <header className="jp-payment-list-heading">
          <div>
            <h1>이용목록</h1>
          </div>
          <button type="button" className="jp-pay-button primary" onClick={() => navigate("/start")}><Plus size={17} /> 새 결제요청</button>
        </header>

        <div className="jp-payment-toolbar">
          <div className="jp-payment-tabs" role="group" aria-label="결제 상태 필터">
            {(Object.keys(TAB_LABEL) as TabKey[]).map((key) => (
              <button key={key} type="button" aria-pressed={tab === key} onClick={() => setTab(key)}>
                {TAB_LABEL[key]} <b>{counts[key]}</b>
              </button>
            ))}
          </div>
          <div className="jp-payment-search-row">
            <label className="jp-payment-search">
              <Search size={17} aria-hidden="true" />
              <input type="search" aria-label="거래처 또는 요청명 검색" placeholder="거래처 또는 요청명으로 검색" value={query} onChange={(e) => setQuery(e.target.value)} />
            </label>
            <button type="button" className="jp-payment-refresh" aria-label="이용목록 새로고침" onClick={loadPayments} disabled={loading}><RefreshCw size={15} /><span>새로고침</span></button>
          </div>
        </div>

        {SHINHAN_NOTICE && (
          <div className="jp-payment-notice">
            <Info size={17} />
            <p><strong>신한카드 결제 제한 안내</strong><br />현재 PG 정책으로 신한카드 결제가 지원되지 않습니다. 다른 카드로 결제해 주세요.</p>
          </div>
        )}

        {error && <div className="jp-payment-error" role="alert">{error} <button type="button" className="underline ml-2" onClick={loadPayments}>다시 불러오기</button></div>}

        <div className="jp-payment-result" aria-live="polite"><span>{TAB_LABEL[tab]} <strong>{searched.length}건</strong></span><span>승인된 요청부터 결제할 수 있어요</span></div>
        <div className="jp-payment-items">
          {loading && <div className="jp-payment-empty" role="status"><RefreshCw size={25} strokeWidth={1.5} /><h2>이용목록을 불러오고 있어요</h2></div>}
          {!loading && !error && searched.length === 0 && (
            <div className="jp-payment-empty">
              <FileText size={31} strokeWidth={1.3} />
              <h2>{query.trim() ? "검색 결과가 없습니다." : emptyMessage}</h2>
              <p>{query.trim() ? "거래처 이름이나 요청명을 다시 확인해 주세요." : "결제요청을 등록하면 진행 상황을 여기서 확인할 수 있어요."}</p>
              {query.trim() ? <button type="button" className="jp-pay-button" onClick={() => setQuery("")}>검색 초기화</button> : <button type="button" className="jp-pay-button" onClick={() => navigate("/start")}>결제요청 등록 <ArrowRight size={15} /></button>}
            </div>
          )}

          {searched.map((p) => {
            const isApproved = p.status === "approved";
            const isRequested = p.status === "requested";
            const isFailedOrRejected = p.status === "failed" || p.status === "rejected";
            const canEdit = isRequested || isFailedOrRejected;
            const meta = statusMeta(p.status);
            const b = getAmountBreakdown(p);
            const displayCat = displayServiceName(String(p.category || "").trim());
            const counterparty = pickCounterpartyName(p);
            const primaryTitle = displayCat || counterparty
              ? [displayCat || null, counterparty || null].filter(Boolean).join(" · ")
              : displayServiceName(p.title) || "이체 결제";
            const secondaryTitle = p.title && primaryTitle !== displayServiceName(p.title) ? displayServiceName(p.title) : null;
            const statusHelp = isApproved ? "승인된 요청입니다. 카드결제를 진행해 주세요." : isRequested ? "제출하신 서류와 거래 내용을 검토하고 있어요." : isFailedOrRejected ? "요청 내용을 확인하고 필요한 부분을 수정해 주세요." : "완료된 결제내역입니다.";

            return (
              <article key={p.id} className={`jp-payment-item ${isApproved ? "is-approved" : ""}`}>
                <div className="jp-payment-item-main">
                  <div>
                    <div className="jp-payment-item-tags">
                      <span className={`jp-payment-item-status ${meta.cls}`}>{meta.label}</span>
                      {p.split_count && p.split_index ? <span>분할 {p.split_index} / {p.split_count}</span> : null}
                    </div>
                    <h2>{primaryTitle}</h2>
                    {secondaryTitle && <p className="jp-payment-item-subtitle">{secondaryTitle}</p>}
                    <div className="jp-payment-item-date">신청일 <span className="ml-2">{fmtDateTime(p.created_at)}</span></div>
                    {p.approval_status && <div className="jp-payment-item-date">승인상태 <span className="ml-2">{({ pending: "승인 대기", requested: "검토 대기", approved: "승인 완료", rejected: "승인 거절" } as Record<string, string>)[p.approval_status.toLowerCase()] || p.approval_status}</span></div>}
                  </div>
                  <div className="jp-payment-item-amount">
                    <span>{b.hasTotal ? "총 결제금액" : "요청 원금"}</span>
                    <strong>{fmtAmount(b.total)}</strong>
                    {b.hasBreakdown && <dl className="jp-payment-breakdown">
                      <div><dt>원금</dt><dd>{fmtAmount(b.principal)}</dd></div>
                      {b.fee !== null && <div><dt>수수료</dt><dd>{fmtAmount(b.fee)}</dd></div>}
                      {b.vat !== null && <div><dt>부가세</dt><dd>{fmtAmount(b.vat)}</dd></div>}
                    </dl>}
                  </div>
                </div>
                {p.reject_reason && <div className="jp-payment-rejection"><strong>거절 사유</strong> {p.reject_reason}</div>}
                <div className="jp-payment-item-footer">
                  <p>{statusHelp}</p>
                  <div>
                    {canEdit && <button type="button" onClick={() => goEdit(p.id)} className="jp-pay-button">수정하기</button>}
                    <button type="button" disabled={!isApproved || payingId === p.id} onClick={() => handlePay(p)} className="jp-pay-button primary">
                      {payingId === p.id ? "진행 중..." : "결제하기"}
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      {consentOpen && consentTarget && (
        <div className="jp-payment-dialog fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div role="dialog" aria-modal="true" aria-labelledby="jp-pay-vat-title" className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h2 id="jp-pay-vat-title" className="text-lg font-bold text-slate-900">부가세 중복 환급 불가 안내</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-700">
              부가세 처리는 거래당사자가 발급한 세금계산서/현금영수증 기준으로 진행해 주세요.
              당사 카드결제건을 통해 추가 부가세 환급을 시도할 경우 세법위반이 발생할 수 있습니다.
            </p>
            <label className="mt-4 flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" className="mt-0.5 h-4 w-4" checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)} />
              위 내용을 확인했으며 동의합니다.
            </label>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" className="jp-pay-button" onClick={() => {
                if (consentSaving) return;
                setConsentOpen(false);
                setConsentTarget(null);
                setConsentChecked(false);
              }}>취소</button>
              <button type="button" disabled={!consentChecked || consentSaving} onClick={handleConsentConfirm} className="jp-pay-button primary">{consentSaving ? "처리 중..." : "동의하기"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
